import { ConnectionItem, Flex, Image, Text } from "@wayflyer/flyui";
import type { ConnectionSchemas } from "api-types";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { connectByPath } from "../../../data-sources";
import { useCustomerAppConfig } from "undercarriage";

import { usePlatformConnectorProperties, useCompleteConnectionProps } from "../../queries";
import { useConnectionFlowsConfig } from "../../config";
import { COMMON_MESSAGES } from "../../common-messages";
import { ConnectionsList } from "../../common/connections";
import { ConnectorPropertiesLoading } from "../../common/connector-properties-loading";
import { OAuthErrorBanner } from "../../common/oauth-error-banner";
import { useCancelConnectionOnError } from "../../common/use-cancel-connection-on-error";
import { PropertySelector } from "../../common/property-selector";
import { MESSAGES } from "../../messages";

const CONNECTOR_SLUG = "tiktok";
const CONNECTOR_NAME = "TikTok";

const TikTokConnectContent = ({ onInitiateConnection }: { onInitiateConnection?: () => void }) => {
  const intl = useIntl();

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { connectorsApiUrl, defaultCallbackPath } = useConnectionFlowsConfig();
  const { data: connectorProperties } = usePlatformConnectorProperties(
    customerDomain,
    CONNECTOR_SLUG,
    defaultCallbackPath,
    connectorsApiUrl
  );
  const [connectionInProgress, setConnectionInProgress] = useState(false);

  const renamedConnectorProperties = {
    ...connectorProperties,
  };

  return (
    <Flex direction="column" gap="8">
      <Flex direction="column" gap="8" align="flex-start" justify="space-between">
        <OAuthErrorBanner connectorSlug={CONNECTOR_SLUG} />
        <Text>{intl.formatMessage(MESSAGES.marketingDescription)}</Text>
        <ConnectionsList
          connectorProperties={connectorProperties}
          connectForm={
            <ConnectionItem
              logo={
                renamedConnectorProperties.connectionLogoSrc ? (
                  <Image src={renamedConnectorProperties.connectionLogoSrc} />
                ) : null
              }
              label={renamedConnectorProperties.name}
              loading={connectionInProgress}
              actions={[
                {
                  content: intl.formatMessage(COMMON_MESSAGES.connectAccount),
                  variant: "Tertiary",
                  onAction: () => {
                    onInitiateConnection?.();
                    setConnectionInProgress(true);
                    connectByPath(connectorProperties?.connectPath);
                  },
                },
              ]}
            />
          }
        />
      </Flex>
    </Flex>
  );
};

const TikTokSelectContent = ({
  completeConnectionProps,
}: {
  completeConnectionProps: ConnectionSchemas.CompleteConnectionPropsSchema;
}) => {
  const intl = useIntl();
  return (
    <Flex direction="column" gap="8">
      <Text>{intl.formatMessage(MESSAGES.selectAccounts)}</Text>
      <PropertySelector connectorSlug={CONNECTOR_SLUG} completeConnectionProps={completeConnectionProps} />
    </Flex>
  );
};

export const TikTokConnect = ({ onInitiateConnection }: { onInitiateConnection?: () => void }) => {
  const [searchParams] = useSearchParams();
  const completeConnection = searchParams.get("complete-connection");
  const oAuthError = searchParams.get("oauth-error");

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { completeConnectionApiUrl } = useConnectionFlowsConfig();
  const { data: completeConnectionProps, isLoading } = useCompleteConnectionProps(
    customerDomain,
    completeConnectionApiUrl,
    { enabled: !!completeConnection }
  );

  useCancelConnectionOnError(CONNECTOR_SLUG, completeConnectionProps, isLoading);

  if (oAuthError || completeConnection !== CONNECTOR_SLUG) {
    return <TikTokConnectContent onInitiateConnection={onInitiateConnection} />;
  }
  if (isLoading || !completeConnectionProps) {
    return <ConnectorPropertiesLoading connectorName={CONNECTOR_NAME} />;
  }
  // A silent reconnect that reuses an existing connection has nothing to select - the connection
  // is already live, so skip straight past the selector instead of showing an empty one.
  return completeConnectionProps.firstConnection ? (
    <TikTokSelectContent completeConnectionProps={completeConnectionProps} />
  ) : (
    <TikTokConnectContent onInitiateConnection={onInitiateConnection} />
  );
};
